import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/layout';
import { Card, Button, Badge, Table, AppLoader } from '../components/ui';
import type { Column } from '../components/ui/Table';
import useProvidersStore from '../stores/providersStore';
import {
	providersApi,
	extractPaginationInfo,
	getPrimaryLocation,
	type ProviderDetailResponse,
	type ProviderAppointment,
	type AppointmentStatus,
} from '../api';

type LoadingState = 'idle' | 'loading' | 'error';

// Helper to get badge variant for appointment status
const getStatusBadgeVariant = (
	status: AppointmentStatus
): 'success' | 'warning' | 'danger' | 'info' | 'primary' => {
	switch (status) {
		case 'COMPLETED':
			return 'success';
		case 'CONFIRMED':
			return 'info';
		case 'PENDING':
			return 'warning';
		case 'CANCELLED':
			return 'danger';
		default:
			return 'primary';
	}
};

// Helper to format status for display
const formatStatus = (status: AppointmentStatus): string => {
	return status.charAt(0) + status.slice(1).toLowerCase();
};

const ProviderDetail = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	// Get cached provider from store (for instant display while API loads)
	const cachedProviders = useProvidersStore(state => state.providers);
	const setProviders = useProvidersStore(state => state.setProviders);
	const numericId = id ? parseInt(id, 10) : null;
	const cachedProvider = numericId ? cachedProviders.find(p => p.id === numericId) : null;

	// Local state for API data
	const [provider, setProvider] = useState<ProviderDetailResponse | null>(null);
	const [loadingState, setLoadingState] = useState<LoadingState>('idle');
	const [error, setError] = useState<string | null>(null);
	const [hasFetched, setHasFetched] = useState(false);

	// Fetch provider from API
	useEffect(() => {
		if (!numericId) return;

		const fetchProvider = async () => {
			setLoadingState('loading');
			setError(null);

			try {
				const data = await providersApi.getById(numericId);
				setProvider(data);
				setLoadingState('idle');
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to load provider');
				setLoadingState('error');
			} finally {
				setHasFetched(true);
			}
		};

		fetchProvider();
	}, [numericId]);

	// Background fetch providers list if store is empty (for Edit form pre-fill)
	useEffect(() => {
		if (cachedProviders.length === 0) {
			const fetchProvidersList = async () => {
				try {
					const response = await providersApi.list({ page: 0, size: 20 });
					const paginationInfo = extractPaginationInfo(response);
					setProviders(response.content, paginationInfo);
				} catch {
					// Silent fail - this is a background fetch for convenience
				}
			};

			fetchProvidersList();
		}
	}, [cachedProviders.length, setProviders]);

	// Use API data if available, otherwise fall back to cached data for basic info
	const displayProvider = provider || cachedProvider;

	// Show loading state (while fetching or before fetch starts)
	if ((loadingState === 'loading' || !hasFetched) && !displayProvider) {
		return (
			<AppLayout>
				<AppLoader message="Loading provider details" variant="inline" />
			</AppLayout>
		);
	}

	// Show error state
	if (loadingState === 'error' && !displayProvider) {
		return (
			<AppLayout>
				{/* Back Link */}
				<button
					type="button"
					className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-5 text-sm font-semibold transition-all hover:gap-2 cursor-pointer"
					onClick={() => navigate('/providers')}
				>
					<span>&larr;</span>
					<span>Back to Providers</span>
				</button>

				<Card>
					<div className="text-center py-12">
						<div className="text-6xl mb-4">&#9888;&#65039;</div>
						<h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
							Failed to Load Provider
						</h2>
						<p className="text-gray-500 dark:text-gray-400 mb-6">
							{error || 'An unexpected error occurred'}
						</p>
						<div className="flex justify-center gap-3">
							<Button variant="primary" onClick={() => window.location.reload()}>
								Try Again
							</Button>
							<Button variant="secondary" onClick={() => navigate('/providers')}>
								Go to Providers List
							</Button>
						</div>
					</div>
				</Card>
			</AppLayout>
		);
	}

	// Show not found state
	if (!displayProvider) {
		return (
			<AppLayout>
				{/* Back Link */}
				<button
					type="button"
					className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-5 text-sm font-semibold transition-all hover:gap-2 cursor-pointer"
					onClick={() => navigate('/providers')}
				>
					<span>&larr;</span>
					<span>Back to Providers</span>
				</button>

				<Card>
					<div className="text-center py-12">
						<div className="text-6xl mb-4 opacity-30">&#128100;</div>
						<h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
							Provider Not Found
						</h2>
						<p className="text-gray-500 dark:text-gray-400 mb-6">
							This provider doesn't exist or the data hasn't been loaded yet.
							<br />
							Please navigate from the providers list to view provider details.
						</p>
						<Button variant="primary" onClick={() => navigate('/providers')}>
							Go to Providers List
						</Button>
					</div>
				</Card>
			</AppLayout>
		);
	}

	// Appointment table columns
	const appointmentColumns: Column<ProviderAppointment>[] = [
		{
			key: 'appointmentDateTime',
			header: 'Date & Time',
			sortable: true,
			render: value =>
				new Date(value).toLocaleDateString('en-US', {
					month: 'short',
					day: 'numeric',
					year: 'numeric',
					hour: 'numeric',
					minute: '2-digit',
				}),
		},
		{
			key: 'serviceDescription',
			header: 'Service',
			sortable: true,
		},
		{
			key: 'durationMinutes',
			header: 'Duration',
			render: value => `${value} min`,
		},
		{
			key: 'amount',
			header: 'Amount',
			sortable: true,
			render: value => (
				<span className="font-semibold text-green-600 dark:text-green-400">
					${value.toLocaleString()}
				</span>
			),
		},
		{
			key: 'status',
			header: 'Status',
			render: value => (
				<Badge variant={getStatusBadgeVariant(value as AppointmentStatus)}>
					{formatStatus(value as AppointmentStatus)}
				</Badge>
			),
		},
	];

	// Get data from API response (provider) or fall back to cached data
	const totalAppointments = provider?.totalAppointments ?? cachedProvider?.totalAppointments ?? 0;
	const totalRevenue = provider?.totalRevenue ?? cachedProvider?.totalRevenue ?? 0;
	const recentAppointments = provider?.recentAppointments ?? [];
	const locations = provider?.locations ?? cachedProvider?.locations ?? [];

	// Get primary location for display
	const primaryLocation = getPrimaryLocation(locations);

	// Get completed appointments and average rating from API
	const completedAppointments =
		provider?.completedAppointments ?? cachedProvider?.completedAppointments ?? 0;
	const averageRating = provider?.averageRating ?? cachedProvider?.averageRating ?? 0;

	return (
		<AppLayout>
			{/* Back Link */}
			<button
				type="button"
				className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-5 text-sm font-semibold transition-all hover:gap-2 cursor-pointer"
				onClick={() => navigate('/providers')}
			>
				<span>&larr;</span>
				<span>Back to Providers</span>
			</button>

			{/* Provider Header Card */}
			<Card className="mb-5">
				<div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
					<div className="flex-1 min-w-0">
						<div className="flex flex-wrap items-center gap-3 mb-2">
							<h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
								{displayProvider.name}
							</h1>
							<Badge variant={displayProvider.accountStatus ? 'success' : 'danger'}>
								{displayProvider.accountStatus ? 'Active' : 'Inactive'}
							</Badge>
						</div>
						<p className="text-base text-gray-500 dark:text-gray-400">
							{displayProvider.businessName}
							{primaryLocation &&
								` | ${primaryLocation.city}, ${primaryLocation.state}`}
						</p>
					</div>
					<Button
						variant="secondary"
						onClick={() => navigate(`/providers/${numericId}/edit`)}
						className="w-full sm:w-auto flex-shrink-0"
					>
						Edit Profile
					</Button>
				</div>

				{/* KPI Grid - 4 Cards */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
					<div className="bg-gray-50 dark:bg-gray-700/50 p-5 rounded-xl border border-gray-100 dark:border-gray-600 hover:shadow-md transition-all hover:-translate-y-0.5">
						<div className="text-sm text-gray-500 dark:text-gray-400 font-semibold mb-2">
							Total Appointments
						</div>
						<div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
							{totalAppointments}
						</div>
					</div>
					<div className="bg-gray-50 dark:bg-gray-700/50 p-5 rounded-xl border border-gray-100 dark:border-gray-600 hover:shadow-md transition-all hover:-translate-y-0.5">
						<div className="text-sm text-gray-500 dark:text-gray-400 font-semibold mb-2">
							Total Revenue
						</div>
						<div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
							${totalRevenue.toLocaleString()}
						</div>
					</div>
					<div className="bg-gray-50 dark:bg-gray-700/50 p-5 rounded-xl border border-gray-100 dark:border-gray-600 hover:shadow-md transition-all hover:-translate-y-0.5">
						<div className="text-sm text-gray-500 dark:text-gray-400 font-semibold mb-2">
							Completed Jobs
						</div>
						<div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
							{completedAppointments}
						</div>
					</div>
					<div className="bg-gray-50 dark:bg-gray-700/50 p-5 rounded-xl border border-gray-100 dark:border-gray-600 hover:shadow-md transition-all hover:-translate-y-0.5">
						<div className="text-sm text-gray-500 dark:text-gray-400 font-semibold mb-2">
							Average Rating
						</div>
						<div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
							{averageRating > 0 ? `${averageRating} ⭐` : '-'}
						</div>
					</div>
				</div>

				{/* Provider Information Section */}
				<h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
					Provider Information
				</h3>
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
						<div className="text-sm text-gray-500 dark:text-gray-400 font-semibold mb-1">
							BBP Fee
						</div>
						<div className="text-base font-semibold text-gray-900 dark:text-gray-100">
							$ {displayProvider.baseBbpFee}
						</div>
					</div>
					<div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
						<div className="text-sm text-gray-500 dark:text-gray-400 font-semibold mb-1">
							Total BBP Fee (BBP Fee &times; Location)
						</div>
						<div className="text-base font-semibold text-gray-900 dark:text-gray-100">
							$ {displayProvider.bbpFee}
						</div>
					</div>
					<div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
						<div className="text-sm text-gray-500 dark:text-gray-400 font-semibold mb-1">
							Phone
						</div>
						<div className="text-base font-semibold text-gray-900 dark:text-gray-100">
							{displayProvider.phone}
						</div>
					</div>
					<div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
						<div className="text-sm text-gray-500 dark:text-gray-400 font-semibold mb-1">
							Email
						</div>
						<div className="text-base font-semibold text-gray-900 dark:text-gray-100">
							{displayProvider.email}
						</div>
					</div>
					<div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
						<div className="text-sm text-gray-500 dark:text-gray-400 font-semibold mb-1">
							Join Date
						</div>
						<div className="text-base font-semibold text-gray-900 dark:text-gray-100">
							{new Date(displayProvider.joinedDate).toLocaleDateString('en-US', {
								month: 'short',
								year: 'numeric',
							})}
						</div>
					</div>
				</div>
			</Card>

			{/* Provider Locations */}
			<Card className="mb-5">
				<h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
					Provider Locations
				</h2>
				{locations.length > 0 ? (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
						{locations.map(location => (
							<div
								key={location.id}
								className="bg-gray-50 dark:bg-gray-700/50 p-5 rounded-xl border-2 border-gray-200 dark:border-gray-600 hover:border-indigo-500 dark:hover:border-indigo-400 hover:shadow-md transition-all hover:-translate-y-0.5"
							>
								<div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-gray-200 dark:border-gray-600">
									<div className="flex gap-2">
										{location.primary && (
											<Badge variant="primary">Primary</Badge>
										)}
									</div>
								</div>
								<div className="grid grid-cols-2 gap-3">
									<div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
										<div className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide mb-1">
											Street Address
										</div>
										<div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
											{location.address}
										</div>
									</div>
									<div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
										<div className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide mb-1">
											City
										</div>
										<div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
											{location.city}
										</div>
									</div>
									<div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
										<div className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide mb-1">
											State
										</div>
										<div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
											{location.state}
										</div>
									</div>
									<div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
										<div className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide mb-1">
											Zipcode
										</div>
										<div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
											{location.zipCode}
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
				) : (
					<p className="text-gray-500 dark:text-gray-400 italic">
						No locations added yet.
					</p>
				)}
			</Card>

			{/* Recent Appointments */}
			<Card>
				<h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
					Recent Appointments
				</h2>
				<div className="overflow-x-auto -mx-[18px] px-[18px]">
					<Table
						data={recentAppointments}
						columns={appointmentColumns}
						emptyMessage="No recent appointments"
					/>
				</div>
			</Card>

			<div className="mt-10 text-center text-sm text-gray-400 dark:text-gray-500">
				© 2025 Beauty Business Portal. All rights reserved.
			</div>
		</AppLayout>
	);
};

export default ProviderDetail;
